import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import TopFanAssetsPanel from '../components/TopFanAssetsPanel';
import BulkEnvironmentMove from '../components/BulkEnvironmentMove';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';
import {
  PREMIUM_BUNDLE_TYPE,
  ResolvedBundleItem,
  slotForType,
  STICKER_PACK_TYPE,
  THEMES_DECOR_CATEGORY,
} from '../utils/premiumBundleRules';

const loadLottie = (): Promise<any> =>
  new Promise((resolve) => {
    const win = window as any;
    if (win.lottie) return resolve(win.lottie);
    const existing = document.querySelector('script[data-lottie]');
    if (existing) {
      const interval = setInterval(() => {
        if (win.lottie) { clearInterval(interval); resolve(win.lottie); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    script.setAttribute('data-lottie', '1');
    script.onload = () => resolve(win.lottie);
    document.head.appendChild(script);
  });

const LottieAssetPreview: React.FC<{ url: string; size?: number }> = ({ url, size = 120 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);
  const [lottieError, setLottieError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [lottie, res] = await Promise.all([loadLottie(), fetch(url)]);
        if (cancelled || !containerRef.current) return;
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        if (cancelled || !containerRef.current) return;
        animRef.current?.destroy();
        animRef.current = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: data,
        });
      } catch {
        if (!cancelled) setLottieError(true);
      }
    };
    run();
    return () => {
      cancelled = true;
      animRef.current?.destroy();
    };
  }, [url]);

  if (lottieError) return (
    <div className='flex items-center justify-center h-full text-xs text-gray-500'>Preview unavailable</div>
  );
  return <div ref={containerRef} style={{ width: size, height: size }} />;
};

type Environment = 'dev' | 'prod';
type EnvironmentFilter = 'all' | 'prod' | 'dev';
type StatusFilter = 'all' | 'active' | 'inactive';
type CategoryTab = 'PROFILE' | 'GIFT' | 'LIVE' | 'THEMES_DECOR';
type ThemesDecorType = typeof PREMIUM_BUNDLE_TYPE | typeof STICKER_PACK_TYPE;
const CATEGORY_TABS: { key: CategoryTab; label: string }[] = [
  { key: 'PROFILE', label: 'PROFILE' },
  { key: 'GIFT', label: 'GIFT' },
  { key: 'LIVE', label: 'LIVE' },
  { key: 'THEMES_DECOR', label: 'THEMES & DECOR' },
];
const parseCategoryTab = (value: string | null): CategoryTab | null =>
  value === 'PROFILE' || value === 'GIFT' || value === 'LIVE' || value === THEMES_DECOR_CATEGORY ? (value as CategoryTab) : null;

type WalletShopMediaAsset = {
  Type: string;
  Url: string;
  ThumbnailUrl?: string;
};

type WalletShopProduct = {
  ProductKey: string;
  Name: string;
  Environment?: Environment;
  QuantitySold?: number;
  IsActive?: boolean;
  CurrencyType?: 'coin' | 'token';
  PriceCoins?: number;
  PriceTokens?: number;
  MediaAssets?: WalletShopMediaAsset[];
  ThemePreference?: 'adaptive' | 'light' | 'dark';
  createdAt?: string;
  BundleItems?: ResolvedBundleItem[];
  StickerProductKeys?: string[];
};

type AssetItem = {
  id: string;
  productKey: string;
  productName: string;
  environment?: Environment;
  quantitySold: number;
  isActive: boolean;
  currencyType?: 'coin' | 'token';
  priceCoins?: number;
  priceTokens?: number;
  themePreference?: 'adaptive' | 'light' | 'dark';
  asset: WalletShopMediaAsset;       // primary asset (for sticker/gift views)
  assets: WalletShopMediaAsset[];    // all assets (for event gift view)
  createdAt?: string;
  bundleItems?: ResolvedBundleItem[]; // premium bundles: resolved components
  stickerCount?: number;              // sticker packs
};

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

const isImageUrl = (url: string) => /\.(png|jpe?g|webp|gif|svg)$/i.test(url);
// Solid-colour backgrounds store a hex code (e.g. "#ECBCC0") as the light_mode/dark_mode Url.
const isHexColor = (value: string) => /^#[0-9a-f]{3,8}$/i.test((value || '').trim());

// Types whose ThemePreference the app uses to pick the light/dark asset (bulk-editable on the list page).
const THEME_PREFERENCE_PROFILE_TYPES = ['background', 'background_animated', 'chat_themes'];

const profileTypeFilters = [
  { value: 'profile_frame', label: 'Profile Frames' },
  { value: 'profile_frame_animated', label: 'Animated Frames' },
  { value: 'background', label: 'Backgrounds' },
  { value: 'background_animated', label: 'Animated Backgrounds' },
  { value: 'chat_themes', label: 'Chat Themes' },
  { value: 'profile_badge', label: 'Profile Badges' },
  { value: 'profile_animation', label: 'Profile Animations' },
  { value: 'boost', label: 'Boosts' },
  { value: 'utility', label: 'Utility' },
];

const WalletShopProducts: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory: CategoryTab = parseCategoryTab(searchParams.get('category')) || 'PROFILE';
  const initialEnvParam = (searchParams.get('env') || '').toLowerCase();
  const initialEnv: EnvironmentFilter =
    initialEnvParam === 'dev' ? 'dev' : initialEnvParam === 'all' ? 'all' : 'prod';

  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string>('');

  const [selectedEnv, setSelectedEnv] = useState<EnvironmentFilter>(initialEnv);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Multi-select state (sticker view only)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkThemePreference, setBulkThemePreference] = useState<'adaptive' | 'light' | 'dark'>('dark');

  const resolveUserId = () => (localStorage.getItem('wallet_assets_user_id') || '').trim();

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const handleDeleteProduct = useCallback(
    async (productKey: string, environment?: Environment) => {
      const headers = headerWithAuth();
      if (!headers) return;

      const deleteTrackingKey = `${productKey}-${environment || 'prod'}`;
      setDeletingKey(deleteTrackingKey);
      try {
        const res = await fetch(`${walletApiBase}/deleteWalletShopProduct`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ProductKey: productKey,
            Environment: environment || 'prod',
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to delete product');
        toast.success(json.message || 'Product deleted');
        setAssets((prev) =>
          prev.filter((a) => !(a.productKey === productKey && (a.environment || 'prod') === (environment || 'prod')))
        );
        setSelectedKeys((prev) => {
          const next = new Set(prev);
          next.delete(productKey);
          return next;
        });
      } catch (error) {
        toast.error((error as Error).message || 'Failed to delete product');
      } finally {
        setDeletingKey('');
      }
    },
    [headerWithAuth]
  );

  const [categoryKey, setCategoryKey] = useState<CategoryTab>(initialCategory);
  // Themes & Decor (CategoryKey THEMES_DECOR): premium bundles and sticker packs. Kept in ?type= so
  // the editors can send the admin back to the tab they came from.
  const [themesType, setThemesType] = useState<ThemesDecorType>(
    searchParams.get('type') === STICKER_PACK_TYPE ? STICKER_PACK_TYPE : PREMIUM_BUNDLE_TYPE
  );
  const isThemesDecorView = categoryKey === 'THEMES_DECOR';
  const [profileType, setProfileType] = useState<string>('profile_frame');
  const [giftSubFilter, setGiftSubFilter] = useState<'gifts' | 'stickers' | 'event_gifts'>('gifts');
  // LIVE splits into the purchasable entry-animation catalog (wallet shop
  // products) and the Top Fan rank/status assets (a fixed 7-tier Redis
  // catalog, not products) — different backends, so they are separate tabs.
  const [liveSubFilter, setLiveSubFilter] = useState<'entry_animation' | 'top_fans'>('entry_animation');
  const isTopFansView = categoryKey === 'LIVE' && liveSubFilter === 'top_fans';

  const activeCategoryKey =
    categoryKey === 'GIFT' && giftSubFilter === 'stickers'
      ? 'STICKER'
      : categoryKey === 'GIFT' && giftSubFilter === 'event_gifts'
      ? 'EVENT_GIFT'
      : categoryKey;

  const handleCategoryChange = (key: CategoryTab) => {
    setCategoryKey(key);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('category', key);
    nextParams.delete('type');
    if (selectedEnv !== 'prod') {
      nextParams.set('env', selectedEnv);
    } else {
      nextParams.delete('env');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleEnvChange = (env: EnvironmentFilter) => {
    setSelectedEnv(env);
    const nextParams = new URLSearchParams(searchParams);
    if (categoryKey) nextParams.set('category', categoryKey);
    if (env !== 'prod') {
      nextParams.set('env', env);
    } else {
      nextParams.delete('env');
    }
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    const envParam = (searchParams.get('env') || '').toLowerCase();
    const envFromUrl: EnvironmentFilter = envParam === 'dev' ? 'dev' : envParam === 'all' ? 'all' : 'prod';
    setSelectedEnv((prev) => (prev !== envFromUrl ? envFromUrl : prev));
    const catParam = parseCategoryTab(searchParams.get('category'));
    if (catParam) {
      setCategoryKey((prev) => (prev !== catParam ? catParam : prev));
    }
  }, [searchParams]);

  const fetchAssets = useCallback(async () => {
    // Top Fan assets are not wallet shop products — that tab owns its own
    // fetch, so skip the product query entirely (and its UserID toast).
    if (isTopFansView) {
      setAssets([]);
      return;
    }

    const userId = resolveUserId();
    if (!userId) {
      toast.error('UserID is required to fetch products.');
      return;
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setLoading(true);
    setSelectedKeys(new Set());
    try {
      // V1 strips `image` assets from static backgrounds (kept for old app builds that
      // only paint light_mode/dark_mode hex colours) and then drops every product left
      // with no assets — so it lists only the 5 colour backgrounds. The app reads V2.
      const isStaticBackgroundView = activeCategoryKey === 'PROFILE' && profileType === 'background';
      const endpoint = activeCategoryKey === 'EVENT_GIFT' || activeCategoryKey === 'LIVE' || isStaticBackgroundView || isThemesDecorView
        ? `${walletApiBase}/getWalletShopProductsV2`
        : `${walletApiBase}/getWalletShopProducts`;
      // Premium bundles / sticker packs are listed by Type alone (with BundleItems / StickerItems resolved).
      const baseBody = isThemesDecorView
        ? { UserID: userId, Type: themesType, IncludeInactive: true }
        : {
            UserID: userId,
            CategoryKey: activeCategoryKey,
            ...(activeCategoryKey === 'PROFILE' ? { Type: profileType } : {}),
            ...(activeCategoryKey === 'LIVE' ? { Type: 'entry_animation' } : {}),
            IncludeInactive: true,
          };

      // Query products by explicit environment.
      // getWalletShopProductsV2 filters by a single CurrencyType (defaulting to coin-only
      // when omitted) — LIVE entry animations can be priced in either coins or tokens, so
      // fetch both and merge to avoid silently hiding token-priced products from the list.
      const fetchOnce = async (env: Environment, currencyType?: 'coin' | 'token' | 'all') => {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ...baseBody,
            Environment: env,
            ...(currencyType ? { CurrencyType: currencyType } : {}),
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load products');
        const list = (json.data?.products as WalletShopProduct[]) || [];
        return list.map((p) => ({ ...p, Environment: p.Environment || env }));
      };

      // Only V2 filters by CurrencyType, so only V2 needs the coin + token fetch.
      // PROFILE (incl. profile frames) uses V1, which already returns every
      // currency — fetching it twice listed each frame twice.
      const fetchBothCurrencies = endpoint.endsWith('/getWalletShopProductsV2') && activeCategoryKey === 'LIVE';

      const targetEnvs: Environment[] = selectedEnv === 'all' ? ['prod', 'dev'] : [selectedEnv];

      const fetchPromises = targetEnvs.flatMap((env) => {
        if (fetchBothCurrencies) {
          return [fetchOnce(env, 'coin'), fetchOnce(env, 'token')];
        }
        return [
          fetchOnce(
            env,
            activeCategoryKey === 'EVENT_GIFT' ? 'token' : isStaticBackgroundView || isThemesDecorView ? 'all' : undefined
          ),
        ];
      });

      const results = await Promise.all(fetchPromises);
      const fetched = results.flat();

      // One card per (ProductKey + Environment)
      const productsMap = new Map<string, WalletShopProduct>();
      for (const p of fetched) {
        const key = `${p.ProductKey}:::${p.Environment || 'prod'}`;
        if (!productsMap.has(key)) {
          productsMap.set(key, p);
        }
      }
      const products = Array.from(productsMap.values());
      const flattened = products
        .slice()
        .sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        })
        .map((product) => {
          const allAssets = product.MediaAssets || [];
          const primaryAsset =
            allAssets.find((a) => a.Type === 'animation') || allAssets[0] || { Type: 'thumbnail', Url: '' };
          return {
            id: `${product.ProductKey}-${product.Environment || 'prod'}`,
            productKey: product.ProductKey,
            productName: product.Name,
            environment: product.Environment,
            quantitySold: product.QuantitySold ?? 0,
            isActive: product.IsActive ?? true,
            currencyType: product.CurrencyType,
            priceCoins: product.PriceCoins,
            priceTokens: product.PriceTokens,
            themePreference: product.ThemePreference,
            createdAt: product.createdAt,
            asset: primaryAsset,
            assets: allAssets,
            bundleItems: product.BundleItems,
            stickerCount: product.StickerProductKeys?.length,
          };
        });

      setAssets(flattened);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [activeCategoryKey, headerWithAuth, profileType, isTopFansView, isThemesDecorView, themesType, selectedEnv]);

  useEffect(() => {
    if (categoryKey === 'PROFILE') setProfileType('profile_frame');
    setGiftSubFilter('gifts');
    setLiveSubFilter('entry_animation');
  }, [categoryKey]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const visibleAssets = useMemo(() => {
    return assets.filter((item) => {
      if (statusFilter === 'active' && !item.isActive) return false;
      if (statusFilter === 'inactive' && item.isActive) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const matchesName = (item.productName || '').toLowerCase().includes(q);
        const matchesKey = (item.productKey || '').toLowerCase().includes(q);
        if (!matchesName && !matchesKey) return false;
      }
      return true;
    });
  }, [assets, statusFilter, searchQuery]);

  const totalAssets = useMemo(() => assets.length, [assets.length]);

  const [downloadingKey, setDownloadingKey] = useState<string>('');

  const downloadFile = useCallback(async (url: string, trackingKey: string, fallbackName: string) => {
    const filename = url.split('/').pop()?.split('?')[0] || fallbackName;
    setDownloadingKey(trackingKey);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch file');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error('Failed to download file');
    } finally {
      setDownloadingKey('');
    }
  }, []);

  // ── Sticker multi-select helpers ──────────────────────────────────────────

  const toggleSelect = (productKey: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(productKey)) next.delete(productKey);
      else next.add(productKey);
      return next;
    });
  };

  const allSelected = visibleAssets.length > 0 && visibleAssets.every((a) => selectedKeys.has(a.productKey));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(visibleAssets.map((a) => a.productKey)));
    }
  };

  const bulkSetActive = async (isActive: boolean) => {
    const headers = headerWithAuth();
    if (!headers || selectedKeys.size === 0) return;

    setBulkWorking(true);
    const keys = Array.from(selectedKeys);
    let successCount = 0;

    await Promise.all(
      keys.map(async (productKey) => {
        const item = assets.find((a) => a.productKey === productKey);
        try {
          const res = await fetch(`${walletApiBase}/createWalletShopProduct`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              ProductKey: productKey,
              IsActive: isActive,
              Environment: item?.environment || 'prod',
            }),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.message);
          successCount++;
          setAssets((prev) =>
            prev.map((a) => (a.productKey === productKey ? { ...a, isActive } : a))
          );
        } catch {
          // individual failures are surfaced in the final toast
        }
      })
    );

    setBulkWorking(false);
    const failed = keys.length - successCount;
    if (failed === 0) {
      toast.success(`${successCount} sticker(s) marked ${isActive ? 'Active' : 'Inactive'}`);
    } else {
      toast.warn(`${successCount} updated, ${failed} failed`);
    }
    setSelectedKeys(new Set());
  };

  const bulkDelete = async () => {
    const headers = headerWithAuth();
    if (!headers || selectedKeys.size === 0) return;

    setBulkWorking(true);
    const keys = Array.from(selectedKeys);
    let successCount = 0;

    await Promise.all(
      keys.map(async (productKey) => {
        const item = assets.find((a) => a.productKey === productKey);
        try {
          const res = await fetch(`${walletApiBase}/deleteWalletShopProduct`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              ProductKey: productKey,
              Environment: item?.environment || 'prod',
            }),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.message);
          successCount++;
          setAssets((prev) => prev.filter((a) => a.productKey !== productKey));
        } catch {
          // individual failures surfaced in final toast
        }
      })
    );

    setBulkWorking(false);
    const failed = keys.length - successCount;
    if (failed === 0) {
      toast.success(`${successCount} sticker(s) deleted`);
    } else {
      toast.warn(`${successCount} deleted, ${failed} failed`);
    }
    setSelectedKeys(new Set());
  };

  // Premium bundles / sticker packs: one-click activate/deactivate. The server runs its activation
  // check (components exist, right type, have media; price set) and its message is shown on failure.
  const [togglingKey, setTogglingKey] = useState('');
  const handleToggleActive = async (item: AssetItem) => {
    const headers = headerWithAuth();
    if (!headers) return;
    setTogglingKey(item.id);
    try {
      const res = await fetch(`${walletApiBase}/createWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ProductKey: item.productKey, IsActive: !item.isActive, Environment: item.environment || 'prod' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update status');
      setAssets((prev) => prev.map((a) => (a.id === item.id ? { ...a, isActive: !item.isActive } : a)));
      toast.success(`${item.productName} is now ${item.isActive ? 'inactive' : 'active'}`);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update status');
    } finally {
      setTogglingKey('');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const bulkSetThemePreference = async () => {
    const headers = headerWithAuth();
    if (!headers || selectedKeys.size === 0) return;

    // With Env "All" the selection can span dev and prod — one request per environment.
    const keysByEnv = new Map<Environment, string[]>();
    visibleAssets
      .filter((a) => selectedKeys.has(a.productKey))
      .forEach((a) => {
        const env = (a.environment || 'prod') as Environment;
        keysByEnv.set(env, [...(keysByEnv.get(env) || []), a.productKey]);
      });

    setBulkWorking(true);
    let updated = 0;
    let skipped = 0;
    const errors: string[] = [];
    await Promise.all(
      Array.from(keysByEnv.entries()).map(async ([env, keys]) => {
        try {
          const res = await fetch(`${walletApiBase}/bulkUpdateWalletShopProductTheme`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ ProductKeys: keys, ThemePreference: bulkThemePreference, Environment: env }),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.message || 'Failed to update Theme Preference');
          updated += json.data?.Matched ?? 0;
          skipped += json.data?.Skipped ?? 0;
          const keySet = new Set(keys);
          setAssets((prev) =>
            prev.map((a) =>
              keySet.has(a.productKey) && (a.environment || 'prod') === env
                ? { ...a, themePreference: bulkThemePreference }
                : a
            )
          );
        } catch (error) {
          errors.push(`${env}: ${(error as Error).message}`);
        }
      })
    );
    setBulkWorking(false);

    if (errors.length) {
      toast.error(`Theme Preference update failed — ${errors.join('; ')}`);
    } else if (skipped) {
      toast.warn(`Theme set to ${bulkThemePreference} on ${updated} product(s); ${skipped} skipped`);
    } else {
      toast.success(`Theme set to ${bulkThemePreference} on ${updated} product(s)`);
    }
    if (!errors.length) setSelectedKeys(new Set());
  };

  const isStickersView = categoryKey === 'GIFT' && giftSubFilter === 'stickers';
  // Product types the app renders by Theme Preference — these tabs get the bulk theme toolbar.
  const isThemeBulkView = categoryKey === 'PROFILE' && THEME_PREFERENCE_PROFILE_TYPES.includes(profileType);
  const isSelectableView = isStickersView || isThemeBulkView;
  // Selected cards still on screen (search/status filters can hide selected ones) — what bulk actions act on.
  const selectedVisibleAssets = visibleAssets.filter((a) => selectedKeys.has(a.productKey));

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Wallet Assets</h1>
            <p className='text-sm text-gray-400'>All media assets from wallet shop products.</p>
          </div>
          <div className='flex flex-col sm:flex-row items-start sm:items-center gap-3'>
            {/* Top Fan assets are a fixed 7-tier catalog, not a product
                list — an asset count and a "create product" action are both
                meaningless there. */}
            {!isTopFansView && (
              <div className='text-sm text-gray-400'>
                {loading
                  ? 'Loading assets...'
                  : visibleAssets.length !== totalAssets
                  ? `${visibleAssets.length} of ${totalAssets} assets (${selectedEnv.toUpperCase()})`
                  : `${totalAssets} assets (${selectedEnv.toUpperCase()})`}
              </div>
            )}
            {!isTopFansView && (
              <button
                onClick={() =>
                  navigate(
                    isThemesDecorView
                      ? `/products/${themesType === STICKER_PACK_TYPE ? 'sticker-packs' : 'premium-bundles'}/new?env=${
                          selectedEnv === 'dev' ? 'dev' : 'prod'
                        }`
                      : categoryKey === 'LIVE'
                      ? `/products/new?category=LIVE&type=entry_animation&env=${selectedEnv === 'dev' ? 'dev' : 'prod'}`
                      : activeCategoryKey === 'PROFILE'
                        ? // Keep the selected sub-tab (e.g. Animated Frames → profile_frame_animated);
                          // without it the form always defaulted to a static profile_frame.
                          `/products/new?category=PROFILE&type=${profileType}&env=${selectedEnv === 'dev' ? 'dev' : 'prod'}`
                        : `/products/new?category=${activeCategoryKey}&env=${selectedEnv === 'dev' ? 'dev' : 'prod'}`
                  )
                }
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
              >
                {categoryKey === 'GIFT'
                  ? 'Create Gift'
                  : categoryKey === 'LIVE'
                  ? 'Create Entry Animation'
                  : isThemesDecorView
                  ? themesType === STICKER_PACK_TYPE
                    ? 'Create Sticker Pack'
                    : 'Create Premium Bundle'
                  : 'Create Product'}
              </button>
            )}
            <div className='flex items-center gap-2 bg-white/10 border border-white/10 rounded-full p-1'>
              {CATEGORY_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleCategoryChange(key)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition ${
                    categoryKey === key
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {categoryKey === 'PROFILE' && (
          <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
            <div className='flex flex-wrap items-center gap-2'>
              {profileTypeFilters.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setProfileType(type.value)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                    profileType === type.value
                      ? 'border-[#FF9C15] text-white bg-white/10'
                      : 'border-white/10 text-gray-300 hover:text-white hover:border-white/30'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
            {(profileType === 'profile_frame' || profileType === 'profile_frame_animated') && (
              <div className='flex items-center gap-2'>
                <button
                  onClick={() =>
                    navigate(
                      `/products/frames/bulk?type=${profileType}&env=${selectedEnv === 'dev' ? 'dev' : 'prod'}`
                    )
                  }
                  className='px-4 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'
                >
                  Bulk Upload Frames
                </button>
              </div>
            )}
          </div>
        )}

        {categoryKey === 'GIFT' && (
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center gap-2 bg-white/10 border border-white/10 rounded-full p-1'>
              {(['gifts', 'stickers', 'event_gifts'] as const).map((sub) => (
                <button
                  key={sub}
                  onClick={() => setGiftSubFilter(sub)}
                  className={`px-4 py-1 text-xs font-semibold rounded-full transition ${
                    giftSubFilter === sub
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {sub === 'gifts' ? 'Normal Gifts' : sub === 'stickers' ? 'Stickers' : 'Event Gifts'}
                </button>
              ))}
            </div>
            {giftSubFilter === 'stickers' && (
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => navigate('/products/stickers/bulk-update-thumbnails')}
                  className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
                >
                  Bulk Update Thumbnails
                </button>
                <button
                  onClick={() => navigate('/products/stickers/bulk')}
                  className='px-4 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'
                >
                  Bulk Upload Stickers
                </button>
              </div>
            )}
            {giftSubFilter === 'event_gifts' && (
              <button
                onClick={() => navigate('/products/event-gifts/new')}
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'
              >
                + Create Event Gift
              </button>
            )}
          </div>
        )}

        {categoryKey === 'LIVE' && (
          <div className='mb-6 space-y-4'>
            <div className='flex items-center gap-2 bg-white/10 border border-white/10 rounded-full p-1 w-fit'>
              {([
                { key: 'entry_animation', label: 'Entry Animation' },
                { key: 'top_fans', label: 'Top Fans Live' },
              ] as const).map((sub) => (
                <button
                  key={sub.key}
                  onClick={() => setLiveSubFilter(sub.key)}
                  className={`px-4 py-1 text-xs font-semibold rounded-full transition ${
                    liveSubFilter === sub.key
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>
            {liveSubFilter === 'entry_animation' && (
              <p className='text-xs text-gray-400'>
                Purchasable Live join-celebration animations. A viewer buys one with tokens, equips it in-app, and it
                plays when they join a livestream.
              </p>
            )}
          </div>
        )}

        {isThemesDecorView && (
          <div className='mb-6 space-y-4'>
            <div className='flex items-center gap-2 bg-white/10 border border-white/10 rounded-full p-1 w-fit'>
              {([
                { key: PREMIUM_BUNDLE_TYPE, label: 'Premium Packs' },
                { key: STICKER_PACK_TYPE, label: 'Sticker Packs' },
              ] as const).map((sub) => (
                <button
                  key={sub.key}
                  onClick={() => {
                    setThemesType(sub.key);
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.set('type', sub.key);
                    setSearchParams(nextParams, { replace: true });
                  }}
                  className={`px-4 py-1 text-xs font-semibold rounded-full transition ${
                    themesType === sub.key
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>
            <p className='text-xs text-gray-400'>
              {themesType === PREMIUM_BUNDLE_TYPE
                ? 'One purchase granting a profile frame, background, chat theme, sticker pack and entry animation. Shown under Wallet Shop → Themes & Decor → Premium Packs.'
                : 'Groups of existing stickers sold as one product, and used as the sticker pack of a premium bundle.'}
            </p>
          </div>
        )}

        {/* Environment, Status, and Search Controls */}
        {!isTopFansView && (
          <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 bg-white/5 border border-white/10 rounded-2xl p-3.5 sm:p-4'>
            <div className='flex flex-wrap items-center gap-3 sm:gap-4'>
              {/* Environment Filter */}
              <div className='flex items-center gap-2'>
                <span className='text-xs font-medium text-gray-400'>Env:</span>
                <div className='flex items-center bg-black/40 border border-white/10 rounded-lg p-0.5 sm:p-1'>
                  {(
                    [
                      { value: 'prod', label: 'Prod', dot: 'bg-emerald-400' },
                      { value: 'dev', label: 'Dev', dot: 'bg-amber-400' },
                      { value: 'all', label: 'All', dot: 'bg-sky-400' },
                    ] as const
                  ).map((env) => (
                    <button
                      key={env.value}
                      onClick={() => handleEnvChange(env.value)}
                      className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1 text-xs font-semibold rounded-md transition ${
                        selectedEnv === env.value
                          ? 'bg-white/20 text-white shadow'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${env.dot}`} />
                      {env.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div className='flex items-center gap-2'>
                <span className='text-xs font-medium text-gray-400'>Status:</span>
                <div className='flex items-center bg-black/40 border border-white/10 rounded-lg p-0.5 sm:p-1'>
                  {(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ] as const
                  ).map((st) => (
                    <button
                      key={st.value}
                      onClick={() => setStatusFilter(st.value)}
                      className={`px-2.5 sm:px-3 py-1 text-xs font-semibold rounded-md transition ${
                        statusFilter === st.value
                          ? 'bg-white/20 text-white shadow'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {st.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Instant Search Box */}
            <div className='relative w-full md:w-64'>
              <input
                type='text'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='Search name or key...'
                className='w-full pl-8 pr-7 py-1.5 text-xs bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]'
              />
              <span className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none'>
                🔍
              </span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs px-1'
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}

        {isTopFansView && <TopFanAssetsPanel />}

        {/* Theme Preference bulk toolbar (Backgrounds / Animated Backgrounds / Chat Themes) */}
        {isThemeBulkView && visibleAssets.length > 0 && (
          <div className='flex flex-wrap items-center gap-3 mb-5 bg-white/5 border border-white/10 rounded-xl px-4 py-3'>
            <label className='flex items-center gap-2 cursor-pointer select-none'>
              <input
                type='checkbox'
                checked={allSelected}
                onChange={toggleSelectAll}
                className='w-4 h-4 accent-[#FF9C15] cursor-pointer'
              />
              <span className='text-xs text-gray-300'>{allSelected ? 'Deselect all' : 'Select all'}</span>
            </label>
            <span className='text-xs text-gray-400'>
              {selectedKeys.size > 0 ? `${selectedKeys.size} selected` : 'Select products to bulk-update Theme Preference or move them between environments'}
            </span>
            <div className='flex items-center gap-2 ml-auto flex-wrap'>
              <label className='text-xs text-gray-400'>Theme Preference</label>
              <select
                value={bulkThemePreference}
                onChange={(e) => setBulkThemePreference(e.target.value as 'adaptive' | 'light' | 'dark')}
                className='px-3 py-1.5 text-xs bg-white/10 border border-white/20 rounded-lg text-white'
              >
                <option value='adaptive' className='bg-[#1E1E2D]'>Adaptive (Matches app theme)</option>
                <option value='light' className='bg-[#1E1E2D]'>Light (Always use light version)</option>
                <option value='dark' className='bg-[#1E1E2D]'>Dark (Always use dark version)</option>
              </select>
              <button
                onClick={bulkSetThemePreference}
                disabled={bulkWorking || selectedKeys.size === 0}
                className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#FF9C15]/80 hover:bg-[#FF9C15] text-white transition disabled:opacity-50'
              >
                {bulkWorking ? 'Updating...' : `Apply to ${selectedKeys.size} selected`}
              </button>
            </div>
          </div>
        )}

        {/* Sticker bulk-action toolbar */}
        {isStickersView && visibleAssets.length > 0 && (
          <div className='flex flex-wrap items-center gap-3 mb-5 bg-white/5 border border-white/10 rounded-xl px-4 py-3'>
            <label className='flex items-center gap-2 cursor-pointer select-none'>
              <input
                type='checkbox'
                checked={allSelected}
                onChange={toggleSelectAll}
                className='w-4 h-4 accent-[#FF9C15] cursor-pointer'
              />
              <span className='text-xs text-gray-300'>
                {allSelected ? 'Deselect all' : 'Select all'}
              </span>
            </label>
            {selectedKeys.size > 0 && (
              <>
                <span className='text-xs text-gray-400'>{selectedKeys.size} selected</span>
                <div className='flex items-center gap-2 ml-auto flex-wrap'>
                  <button
                    onClick={() => bulkSetActive(true)}
                    disabled={bulkWorking}
                    className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-500/80 hover:bg-emerald-500 text-white transition disabled:opacity-50'
                  >
                    Set Active
                  </button>
                  <button
                    onClick={() => bulkSetActive(false)}
                    disabled={bulkWorking}
                    className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500/80 hover:bg-amber-500 text-white transition disabled:opacity-50'
                  >
                    Set Inactive
                  </button>
                  <button
                    onClick={bulkDelete}
                    disabled={bulkWorking}
                    className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-50'
                  >
                    {bulkWorking ? 'Working...' : `Delete (${selectedKeys.size})`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Bulk Move to Prod / Move to Dev — every tab with bulk selection */}
        {isSelectableView && (
          <BulkEnvironmentMove
            key={`${activeCategoryKey}-${profileType}`}
            items={selectedVisibleAssets}
            disabled={bulkWorking}
            onMoved={() => {
              setSelectedKeys(new Set());
              fetchAssets();
            }}
          />
        )}

        {isTopFansView ? null : loading ? (
          <div className='text-sm text-gray-400'>Fetching assets...</div>
        ) : assets.length === 0 ? (
          <div className='p-8 text-center bg-white/5 border border-white/10 rounded-2xl'>
            <p className='text-sm text-gray-300'>No assets found in environment &ldquo;{selectedEnv.toUpperCase()}&rdquo;.</p>
            {selectedEnv !== 'all' && (
              <button
                onClick={() => handleEnvChange('all')}
                className='mt-3 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
              >
                Switch to All Environments
              </button>
            )}
          </div>
        ) : visibleAssets.length === 0 ? (
          <div className='p-8 text-center bg-white/5 border border-white/10 rounded-2xl'>
            <p className='text-sm text-gray-300'>No assets match the current filter criteria.</p>
            <button
              onClick={() => {
                setStatusFilter('all');
                setSearchQuery('');
              }}
              className='mt-3 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
            >
              Clear Status & Search Filters
            </button>
          </div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
            {visibleAssets.map((item) => {
              const previewAsset =
                item.assets.find((a) => a.Type === 'thumbnail' || a.Type === 'image' || isImageUrl(a.Url)) ||
                item.asset;
              const assetIsImage = previewAsset.Type === 'image' || previewAsset.Type === 'thumbnail' || isImageUrl(previewAsset.Url);
              const colorSwatches = assetIsImage ? [] : item.assets.filter((a) => isHexColor(a.Url));
              const isSelected = selectedKeys.has(item.productKey);

              return (
                <div
                  key={item.id}
                  className={`relative bg-white/5 backdrop-blur-sm border rounded-2xl overflow-hidden shadow-lg transition ${
                    isSelectableView && isSelected
                      ? 'border-[#FF9C15]/70 ring-1 ring-[#FF9C15]/40'
                      : 'border-white/10'
                  }`}
                >
                  {isThemeBulkView && (
                    <label className='absolute top-2 left-2 z-20 flex items-center gap-1.5 bg-black/60 rounded-md px-2 py-1 cursor-pointer select-none'>
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => toggleSelect(item.productKey)}
                        className='w-4 h-4 accent-[#FF9C15] cursor-pointer'
                      />
                      <span className='text-[11px] text-white'>Select</span>
                    </label>
                  )}
                  {/* Preview area */}
                  {activeCategoryKey === 'LIVE' ||
                  (activeCategoryKey === 'PROFILE' &&
                    (profileType === 'profile_animation' ||
                      profileType === 'profile_frame_animated' ||
                      profileType === 'background_animated')) ? (
                    <div className='h-40 bg-black/20 flex items-center justify-center'>
                      {(() => {
                        const webmAsset = item.assets.find((a) => a.Type === 'webm');
                        const iosAsset = item.assets.find((a) => a.Type === 'ios_alpha');
                        const previewUrl = webmAsset?.Url || iosAsset?.Url;
                        return previewUrl ? (
                          <video src={previewUrl} className='h-full w-full object-contain' muted loop autoPlay playsInline />
                        ) : (
                          <span className='text-xs text-gray-500'>No preview</span>
                        );
                      })()}
                    </div>
                  ) : activeCategoryKey === 'EVENT_GIFT' ? (
                    <div className='bg-white/5 px-4 pt-4 pb-2 space-y-2'>
                      {(() => {
                        const animAsset = item.assets.find((a) => a.Type === 'animation');
                        const bgAsset = item.assets.find((a) => a.Type === 'background');
                        return (
                          <>
                            {animAsset && (
                              <div className='flex items-center justify-center rounded-lg overflow-hidden bg-black/30 border border-white/10' style={{ height: 140 }}>
                                <LottieAssetPreview url={animAsset.Url} size={120} />
                              </div>
                            )}
                            {item.assets.map((a) => (
                              <div key={a.Type} className='flex items-center justify-between gap-2 bg-white/5 rounded-lg px-3 py-2'>
                                <div className='min-w-0'>
                                  <span className='text-[11px] font-semibold text-[#FF9C15] uppercase tracking-wide'>{a.Type}</span>
                                  <p className='text-[10px] text-gray-400 font-mono truncate'>{a.Url.split('/').pop()}</p>
                                </div>
                                <button
                                  onClick={() => downloadFile(a.Url, `${item.productKey}-${a.Type}`, `${item.productKey}-${a.Type}`)}
                                  disabled={downloadingKey === `${item.productKey}-${a.Type}`}
                                  className='shrink-0 px-2 py-1 text-[10px] font-semibold rounded bg-blue-500/80 hover:bg-blue-500 text-white transition disabled:opacity-60'
                                >
                                  {downloadingKey === `${item.productKey}-${a.Type}` ? '…' : '↓'}
                                </button>
                              </div>
                            ))}
                            {bgAsset && (
                              <div className='flex items-center justify-center rounded-lg overflow-hidden bg-black/30 border border-white/10' style={{ height: 100 }}>
                                <LottieAssetPreview url={bgAsset.Url} size={100} />
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                  <div
                    className={`relative h-48 bg-white/10 flex items-center justify-center overflow-hidden ${
                      isStickersView ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => isStickersView && toggleSelect(item.productKey)}
                  >
                    {isStickersView && (
                      <div className='absolute top-2 left-2 z-10'>
                        <input
                          type='checkbox'
                          checked={isSelected}
                          onChange={() => toggleSelect(item.productKey)}
                          onClick={(e) => e.stopPropagation()}
                          className='w-4 h-4 accent-[#FF9C15] cursor-pointer'
                        />
                      </div>
                    )}
                    {colorSwatches.length > 0 ? (
                      <div className='w-full h-full flex'>
                        {colorSwatches.map((a) => (
                          <div
                            key={a.Type}
                            className='flex-1 h-full flex items-end justify-center pb-2'
                            style={{ backgroundColor: a.Url.trim() }}
                          >
                            <span className='px-2 py-0.5 rounded-full bg-black/40 text-[10px] text-white'>
                              {a.Type} · {a.Url.trim()}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : assetIsImage ? (
                      <img
                        src={previewAsset.Url}
                        alt={`${item.productName} ${previewAsset.Type}`}
                        className='w-full h-full object-cover'
                      />
                    ) : (
                      <div className='text-sm text-gray-300 flex flex-col items-center gap-2'>
                        <span className='px-3 py-1 bg-white/10 rounded-full'>{previewAsset.Type}</span>
                        <span className='text-xs text-gray-500'>Preview not available</span>
                      </div>
                    )}
                  </div>
                  )}

                  <div className='p-4 space-y-2'>
                    <div className='text-sm font-semibold text-white'>{item.productName}</div>
                    <div className='text-xs text-gray-400'>Key: {item.productKey}</div>
                    <div className='flex items-center gap-2 text-xs flex-wrap'>
                      <span className='text-gray-400'>QuantitySold: {item.quantitySold}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full border ${
                          item.isActive
                            ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                            : 'text-red-300 border-red-400/40 bg-red-500/10'
                        }`}
                      >
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full border text-[11px] font-mono ${
                          item.environment === 'dev'
                            ? 'text-amber-300 border-amber-400/50 bg-amber-500/10 font-bold'
                            : 'text-slate-300 border-slate-500/40 bg-slate-500/10'
                        }`}
                      >
                        Env: {item.environment || 'prod'}
                      </span>
                      {item.currencyType === 'token' ? (
                        <span className='px-2 py-0.5 rounded-full border text-purple-300 border-purple-400/40 bg-purple-500/10'>
                          💎 Token · {item.priceTokens ?? '—'}
                        </span>
                      ) : (
                        <span className='px-2 py-0.5 rounded-full border text-amber-300 border-amber-400/40 bg-amber-500/10'>
                          🪙 Coin · {item.priceCoins ?? '—'}
                        </span>
                      )}
                      {item.themePreference && (
                        <span className='px-2 py-0.5 rounded-full border text-sky-300 border-sky-400/40 bg-sky-500/10 capitalize'>
                          Theme: {item.themePreference}
                        </span>
                      )}
                      {isThemesDecorView && themesType === STICKER_PACK_TYPE && (
                        <span className='px-2 py-0.5 rounded-full border text-gray-300 border-white/20'>
                          {item.stickerCount ?? 0} stickers
                        </span>
                      )}
                    </div>
                    {isThemesDecorView && themesType === PREMIUM_BUNDLE_TYPE && (
                      <ul className='text-[11px] space-y-0.5'>
                        {(item.bundleItems || []).length === 0 && <li className='text-amber-300'>No components yet</li>}
                        {[...(item.bundleItems || [])]
                          .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))
                          .map((component) => (
                            <li
                              key={`${component.Type}-${component.ProductKey}`}
                              className={component.IsMissing ? 'text-red-300' : 'text-gray-300'}
                            >
                              {slotForType(component.Type)?.label || component.Type}: {component.Name || component.ProductKey}
                              {component.IsMissing ? ' (missing)' : ''}
                              {component.Required === false ? ' · optional' : ''}
                            </li>
                          ))}
                      </ul>
                    )}
                    <a
                      href={item.asset.Url}
                      target='_blank'
                      rel='noreferrer'
                      className='text-xs text-[#FF9C15] hover:underline break-all'
                    >
                      {item.asset.Url}
                    </a>
                    <button
                      onClick={() =>
                        navigate(
                          isThemesDecorView
                            ? `/products/${themesType === STICKER_PACK_TYPE ? 'sticker-packs' : 'premium-bundles'}/edit?productKey=${encodeURIComponent(
                                item.productKey
                              )}&env=${encodeURIComponent(item.environment || 'prod')}`
                            : `/products/update?productKey=${encodeURIComponent(item.productKey)}&category=${encodeURIComponent(
                            activeCategoryKey
                          )}${activeCategoryKey === 'PROFILE' ? `&type=${encodeURIComponent(profileType)}` : ''}&env=${encodeURIComponent(
                            item.environment || 'prod'
                          )}`
                        )
                      }
                      className='mt-3 w-full px-3 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
                    >
                      Update Product
                    </button>
                    {isThemesDecorView && (
                      <button
                        onClick={() => handleToggleActive(item)}
                        disabled={togglingKey === item.id}
                        className={`mt-2 w-full px-3 py-2 text-xs font-semibold rounded-lg text-white transition disabled:opacity-60 ${
                          item.isActive ? 'bg-amber-500/80 hover:bg-amber-500' : 'bg-emerald-500/80 hover:bg-emerald-500'
                        }`}
                      >
                        {togglingKey === item.id ? 'Saving...' : item.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                    {activeCategoryKey !== 'EVENT_GIFT' && (
                    <button
                      onClick={() => downloadFile(item.asset.Url, item.id, `${item.productKey}-asset`)}
                      disabled={downloadingKey === item.id}
                      className='mt-2 w-full px-3 py-2 text-xs font-semibold rounded-lg bg-blue-500/80 hover:bg-blue-500 text-white transition disabled:opacity-60'
                    >
                      {downloadingKey === item.id ? 'Downloading...' : 'Download Asset'}
                    </button>
                    )}
                    {item.asset.ThumbnailUrl && (
                      <button
                        onClick={() =>
                          downloadFile(
                            item.asset.ThumbnailUrl!,
                            `${item.id}-thumb`,
                            `${item.productKey}-thumbnail`
                          )
                        }
                        disabled={downloadingKey === `${item.id}-thumb`}
                        className='mt-2 w-full px-3 py-2 text-xs font-semibold rounded-lg bg-indigo-500/80 hover:bg-indigo-500 text-white transition disabled:opacity-60'
                      >
                        {downloadingKey === `${item.id}-thumb` ? 'Downloading...' : 'Download Thumbnail'}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteProduct(item.productKey, item.environment)}
                      disabled={deletingKey === `${item.productKey}-${item.environment || 'prod'}`}
                      className='mt-2 w-full px-3 py-2 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-60'
                    >
                      {deletingKey === `${item.productKey}-${item.environment || 'prod'}` ? 'Deleting...' : 'Delete Product'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletShopProducts;
